/**
 * Site search — desktop panel toggle + AJAX live results (combobox pattern).
 *
 * Implements the WAI-ARIA 1.2 combobox-with-listbox pattern against
 * the Airport Smart Search AJAX endpoint. DOM focus stays on the input;
 * visual focus on options is tracked via aria-activedescendant.
 *
 * Config is injected by the plugin via wp_localize_script as `window.AirportSearch`.
 */

const HIGHLIGHT_TAG = "mark";

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function highlight(text, query) {
  if (!query) return escapeHtml(text);
  const safe = escapeHtml(text);
  const pattern = new RegExp(
    "(" + query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + ")",
    "gi"
  );
  return safe.replace(pattern, `<${HIGHLIGHT_TAG}>$1</${HIGHLIGHT_TAG}>`);
}

function stripTags(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

/**
 * Initialize the desktop search panel toggle (open/close, escape, focus).
 */
function initPanelToggle() {
  const toggle = document.querySelector(".js-search-toggle");
  const panel = document.getElementById("site-search");
  const input = document.getElementById("site-search-input");
  if (!toggle || !panel) return;

  function openPanel() {
    panel.removeAttribute("hidden");
    toggle.setAttribute("aria-expanded", "true");
    toggle.setAttribute("aria-label", "Close search");
    if (input) requestAnimationFrame(() => input.focus());
  }

  function closePanel() {
    panel.setAttribute("hidden", "");
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Open search");
    toggle.focus();
  }

  toggle.addEventListener("click", () => {
    const isOpen = toggle.getAttribute("aria-expanded") === "true";
    if (isOpen) closePanel();
    else openPanel();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || panel.hasAttribute("hidden")) return;
    // Only close the panel if the listbox isn't showing results.
    // The combobox handler closes the listbox first.
    const listbox = document.getElementById("site-search-results");
    if (listbox && !listbox.hasAttribute("hidden")) return;
    closePanel();
  });

  // Click outside the panel to close
  document.addEventListener("click", (e) => {
    if (panel.hasAttribute("hidden")) return;
    if (panel.contains(e.target) || toggle.contains(e.target)) return;
    closePanel();
  });
}

/**
 * Wire up one live-search form (desktop or mobile). Both share the same code;
 * they differ only in the IDs of their listbox / status region, which are
 * declared via `data-results-id` / `data-status-id` on the form.
 */
function initLiveSearch(form) {
  const config = window.AirportSearch;
  if (!config || !config.ajax_url) return;

  const input = form.querySelector(".js-live-search-input");
  const resultsId = form.getAttribute("data-results-id");
  const statusId = form.getAttribute("data-status-id");
  const errorId = form.getAttribute("data-error-id");
  const listbox = resultsId ? document.getElementById(resultsId) : null;
  const status = statusId ? document.getElementById(statusId) : null;
  const errorEl = errorId ? document.getElementById(errorId) : null;
  if (!input || !listbox) return;

  const minChars = parseInt(config.min_chars, 10) || 2;
  const debounceMs = parseInt(config.debounce_ms, 10) || 350;
  const i18n = config.i18n || {};

  let debounceTimer = null;
  let activeController = null;
  let optionEls = []; // flat array of <li role="option"> elements
  let activeIndex = -1;
  let currentQuery = "";

  function setExpanded(expanded) {
    input.setAttribute("aria-expanded", expanded ? "true" : "false");
    if (expanded) listbox.removeAttribute("hidden");
    else listbox.setAttribute("hidden", "");
  }

  function setActiveDescendant(id) {
    if (id) input.setAttribute("aria-activedescendant", id);
    else input.removeAttribute("aria-activedescendant");
  }

  function announce(text) {
    if (!status) return;
    status.textContent = text;
  }

  function showEmptyError() {
    const msg = (i18n && i18n.empty_term) || "Please enter a search term.";
    if (errorEl) errorEl.textContent = msg;
    input.setAttribute("aria-invalid", "true");
    if (errorId) input.setAttribute("aria-describedby", errorId);
    input.focus();
  }

  function clearEmptyError() {
    if (errorEl && errorEl.textContent) errorEl.textContent = "";
    if (input.hasAttribute("aria-invalid")) {
      input.removeAttribute("aria-invalid");
      input.removeAttribute("aria-describedby");
    }
  }

  function clearResults() {
    listbox.innerHTML = "";
    optionEls = [];
    activeIndex = -1;
    setActiveDescendant("");
    setExpanded(false);
  }

  function updateActive(index, { scrollIntoView = false } = {}) {
    if (index === activeIndex) return;
    optionEls.forEach((el, i) => {
      if (i === index) {
        el.setAttribute("aria-selected", "true");
        el.classList.add("is-active");
        if (scrollIntoView) {
          // Only auto-scroll for keyboard nav — mouse hover implies visibility.
          // Use getBoundingClientRect so nested group/sublist wrappers don't
          // throw off the math (offsetTop is relative to offsetParent, not listbox).
          const elRect = el.getBoundingClientRect();
          const listRect = listbox.getBoundingClientRect();
          if (elRect.top < listRect.top) {
            listbox.scrollTop += elRect.top - listRect.top;
          } else if (elRect.bottom > listRect.bottom) {
            listbox.scrollTop += elRect.bottom - listRect.bottom;
          }
        }
      } else {
        el.setAttribute("aria-selected", "false");
        el.classList.remove("is-active");
      }
    });
    activeIndex = index;
    setActiveDescendant(index >= 0 && optionEls[index] ? optionEls[index].id : "");
  }

  function render(data) {
    const query = data.query || currentQuery;
    const grouped = data.grouped || {};
    const total = parseInt(data.total, 10) || 0;
    const count = parseInt(data.count, 10) || 0;
    listbox.innerHTML = "";
    optionEls = [];
    activeIndex = -1;
    setActiveDescendant("");

    if (!count) {
      const empty = document.createElement("li");
      empty.className = "site-search__results-empty";
      empty.setAttribute("role", "presentation");
      empty.textContent = i18n.no_results || "No results found. Try a different search term.";
      listbox.appendChild(empty);
      setExpanded(true);
      announce(i18n.no_results || "No results found.");
      return;
    }

    const listboxPrefix = listbox.id + "-opt-";
    let optIndex = 0;

    Object.keys(grouped).forEach((typeKey) => {
      const group = grouped[typeKey];
      const groupId = listbox.id + "-group-" + typeKey;

      const groupLi = document.createElement("li");
      groupLi.className = "site-search__results-group";
      groupLi.setAttribute("role", "presentation");

      const title = document.createElement("div");
      title.className = "site-search__results-group-title";
      title.id = groupId + "-title";
      title.textContent = group.label || typeKey;
      groupLi.appendChild(title);

      const sublist = document.createElement("ul");
      sublist.className = "site-search__results-sublist";
      sublist.setAttribute("role", "group");
      sublist.setAttribute("aria-labelledby", title.id);

      (group.items || []).forEach((item) => {
        const li = document.createElement("li");
        li.className = "site-search__results-item";
        li.setAttribute("role", "option");
        li.setAttribute("aria-selected", "false");
        li.id = listboxPrefix + optIndex;
        li.dataset.url = item.url || "";
        li.dataset.type = item.type || "";

        const isPdf =
          item.type === "attachment" ||
          (item.type_label || "").toUpperCase() === "PDF";

        const titleEl = document.createElement("span");
        titleEl.className = "site-search__results-item-title";
        titleEl.innerHTML = highlight(stripTags(item.title || ""), query);

        li.appendChild(titleEl);

        if (item.excerpt) {
          const excerpt = document.createElement("span");
          excerpt.className = "site-search__results-item-excerpt";
          excerpt.innerHTML = item.excerpt; // server-side sanitized, <mark> allowed
          li.appendChild(excerpt);
        }

        if (isPdf) {
          const sr = document.createElement("span");
          sr.className = "u-sr-only";
          sr.textContent = " (opens in new tab)";
          li.appendChild(sr);
          li.dataset.newTab = "1";
        }

        li.addEventListener("mousedown", (e) => e.preventDefault()); // keep focus in input
        li.addEventListener("click", () => navigateTo(item.url, isPdf));
        li.addEventListener("mouseenter", () => updateActive(optIndex));

        sublist.appendChild(li);
        optionEls.push(li);
        optIndex += 1;
      });

      groupLi.appendChild(sublist);
      listbox.appendChild(groupLi);
    });

    // Footer "View all X results" link
    const viewAll = document.createElement("li");
    viewAll.className = "site-search__results-view-all";
    viewAll.setAttribute("role", "option");
    viewAll.id = listboxPrefix + optIndex;
    const viewAllLabel = (i18n.view_all || "View all {n} results").replace(
      "{n}",
      total
    );
    viewAll.textContent = total > count ? viewAllLabel : (i18n.view_all || "View all results");
    viewAll.dataset.url = data.search_url || (config.home_url + "?s=" + encodeURIComponent(query));
    viewAll.addEventListener("mousedown", (e) => e.preventDefault());
    viewAll.addEventListener("click", () => navigateTo(viewAll.dataset.url, false));
    viewAll.addEventListener("mouseenter", () => updateActive(optIndex));
    listbox.appendChild(viewAll);
    optionEls.push(viewAll);

    setExpanded(true);
    announce(
      count === 1
        ? "1 result for " + query
        : count + " results for " + query
    );
  }

  function navigateTo(url, newTab) {
    if (!url) return;
    if (newTab) {
      window.open(url, "_blank", "noopener");
    } else {
      window.location.href = url;
    }
  }

  async function runSearch(query) {
    if (activeController) activeController.abort();
    activeController = new AbortController();

    announce(i18n.loading || "Searching...");
    listbox.classList.add("is-loading");

    const body = new URLSearchParams();
    body.append("action", "airport_live_search");
    body.append("nonce", config.nonce);
    body.append("search_term", query);
    body.append("limit", String(config.results_limit || 8));

    try {
      const res = await fetch(config.ajax_url, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
        signal: activeController.signal,
      });
      const json = await res.json();
      listbox.classList.remove("is-loading");
      if (json && json.success && json.data) {
        render(json.data);
      } else {
        clearResults();
        announce(i18n.error || "Search is temporarily unavailable.");
      }
    } catch (err) {
      if (err.name === "AbortError") return; // superseded, ignore
      listbox.classList.remove("is-loading");
      clearResults();
      announce(i18n.error || "Search is temporarily unavailable.");
    }
  }

  form.addEventListener("submit", (e) => {
    if (input.value.trim() === "") {
      e.preventDefault();
      showEmptyError();
    }
  });

  input.addEventListener("input", () => {
    const value = input.value.trim();
    currentQuery = value;

    clearEmptyError();

    if (debounceTimer) clearTimeout(debounceTimer);

    if (value.length < minChars) {
      clearResults();
      return;
    }

    debounceTimer = setTimeout(() => runSearch(value), debounceMs);
  });

  input.addEventListener("keydown", (e) => {
    const isOpen = input.getAttribute("aria-expanded") === "true";

    // Tab (or Shift+Tab) leaves the combobox — close the results so the
    // scrollable listbox doesn't grab focus next in Chrome's tab order.
    if (e.key === "Tab" && isOpen) {
      clearResults();
      announce("");
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen && currentQuery.length >= minChars) {
          runSearch(currentQuery);
          return;
        }
        if (optionEls.length === 0) return;
        updateActive((activeIndex + 1) % optionEls.length, { scrollIntoView: true });
        break;

      case "ArrowUp":
        e.preventDefault();
        if (optionEls.length === 0) return;
        updateActive(
          activeIndex <= 0 ? optionEls.length - 1 : activeIndex - 1,
          { scrollIntoView: true }
        );
        break;

      case "Home":
        if (!isOpen) return;
        e.preventDefault();
        if (optionEls.length) updateActive(0, { scrollIntoView: true });
        break;

      case "End":
        if (!isOpen) return;
        e.preventDefault();
        if (optionEls.length) updateActive(optionEls.length - 1, { scrollIntoView: true });
        break;

      case "Enter":
        if (activeIndex >= 0 && optionEls[activeIndex]) {
          e.preventDefault();
          const el = optionEls[activeIndex];
          navigateTo(el.dataset.url, el.dataset.newTab === "1");
        }
        // Otherwise let the form submit normally → /?s=query (search.php)
        break;

      case "Escape":
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          clearResults();
          announce("");
        }
        // If already closed, the panel's keydown handler will close the panel.
        break;
    }
  });

  // Close listbox on blur (but only if focus moved outside the form)
  form.addEventListener("focusout", (e) => {
    const next = e.relatedTarget;
    if (next && form.contains(next)) return;
    // Small delay so click-on-option navigation happens before we hide
    setTimeout(() => {
      if (!form.contains(document.activeElement)) clearResults();
    }, 120);
  });
}

export default function initSearch() {
  initPanelToggle();
  document
    .querySelectorAll("form.js-live-search")
    .forEach((form) => initLiveSearch(form));
}
